import React, { useRef } from 'react';
import {
  Box, Grid, TextField, Button,
} from '@mui/material';
import { useTranslation } from 'next-i18next';
import ReCAPTCHA from 'react-google-recaptcha';
import getConfig from 'next/config';
import StyledContainer from 'views/information-module/AffiliateProgramView/common/components/AffiliateProgramForm/AffiliateProgramForm.styles';
import Typography500 from 'views/common/components/UI/Typography500';
import { FormErrors } from 'views/common/components';
import Autocomplete from '@mui/material/Autocomplete';

const {
  publicRuntimeConfig: { envVars },
} = getConfig();

const AffiliateProgramForm = (props) => {
  // props
  const {
    affiliateProgramForm,
    countries,
    states,
    hasRegions,
    handleCountryChange,
    handleStateChange,
    handleSubmitForm,
    handleAffiliateProgramFormChange,
    handleRecaptchChange,
    affiliateProgramErrors,
  } = props;

  // init
  const { t } = useTranslation('fe_er_affiliate_program_page');
  const recaptchaRef = useRef();

  return (
    <StyledContainer maxWidth="xl" id="join-now">
      <Box className="APFFormContiner">
        <Typography500 component="p" variant="h3" className="APFFormHeader">
          {t('fe_er_affiliate_program_page:join_now')}
        </Typography500>
        <Box component="form" noValidate autoComplete="off" mb={3}>
          {affiliateProgramErrors
            && (affiliateProgramErrors.errors?.base
              || affiliateProgramErrors.error_message) && (
              <FormErrors errorMsgs={affiliateProgramErrors} showBaseErrors />
          )}
          <Typography500 component="p" variant="h4" className="APFFormTitle">
            {t('fe_er_affiliate_program_page:affiliate_information')}
          </Typography500>
          <Grid container spacing={2}>
            <Grid item lg={6} md={12} sm={12}>
              <TextField
                value={affiliateProgramForm.company || ''}
                required
                variant="filled"
                fullWidth
                name="company"
                label={t('fe_er_affiliate_program_page:company')}
                className="APFFormInput"
                onChange={handleAffiliateProgramFormChange}
                error={Boolean(affiliateProgramErrors?.errors?.company)}
              />
              {affiliateProgramErrors?.errors && (
                <FormErrors
                  errorMsgs={affiliateProgramErrors}
                  errorField="company"
                />
              )}
            </Grid>
            <Grid item lg={6} md={12} sm={12}>
              <Autocomplete
                id="country-select"
                name="country"
                options={countries}
                value={
                  countries.find(
                    (country) => country.iso2 === affiliateProgramForm.country,
                  ) || ''
                }
                onChange={(event, newValue) => handleCountryChange(event, newValue)}
                onInputChange={() => {}}
                autoHighlight
                disableClearable
                getOptionLabel={(option) => option.name || ''}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    required
                    label={t('fe_er_affiliate_program_page:country')}
                    variant="outlined"
                    inputProps={{
                      ...params.inputProps,
                      autoComplete: 'new-password', // disable autocomplete and autofill
                    }}
                    error={Boolean(affiliateProgramErrors?.errors?.country)}
                    helperText={affiliateProgramErrors?.errors?.country || ''}
                  />
                )}
              />
            </Grid>
            {hasRegions && (
              <>
                <Grid item lg={6} md={12} sm={12}>
                  <Autocomplete
                    id="state-select"
                    name="state"
                    options={states}
                    value={
                      states.find(
                        (state) => state.code === affiliateProgramForm.state,
                      ) || ''
                    }
                    onChange={handleStateChange}
                    onInputChange={() => {}}
                    autoHighlight
                    disableClearable
                    getOptionLabel={(option) => option.name || ''}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        required
                        label={t('fe_er_affiliate_program_page:state')}
                        variant="outlined"
                        inputProps={{
                          ...params.inputProps,
                          autoComplete: 'new-password', // disable autocomplete and autofill
                        }}
                        error={Boolean(affiliateProgramErrors?.errors?.state)}
                        helperText={affiliateProgramErrors?.errors?.state || ''}
                      />
                    )}
                  />
                </Grid>
                <Grid item lg={6} md={12} sm={12}>
                  <TextField
                    value={affiliateProgramForm.zip_code || ''}
                    required
                    label={t('fe_er_affiliate_program_page:zip_code')}
                    variant="filled"
                    fullWidth
                    name="zip_code"
                    onChange={handleAffiliateProgramFormChange}
                    className="APFFormInput"
                    error={Boolean(affiliateProgramErrors?.errors?.zip_code)}
                  />
                  {affiliateProgramErrors?.errors && (
                    <FormErrors
                      errorMsgs={affiliateProgramErrors}
                      errorField="zip_code"
                    />
                  )}
                </Grid>
              </>
            )}
          </Grid>
        </Box>
        <Box component="form" noValidate autoComplete="off">
          <Typography500 component="p" variant="h4" className="APFFormTitle">
            {t('fe_er_affiliate_program_page:contact_person')}
          </Typography500>
          <Grid container spacing={3}>
            <Grid item lg={6} md={12} sm={12}>
              <TextField
                value={affiliateProgramForm.first_name || ''}
                required
                variant="filled"
                fullWidth
                label={t('fe_er_affiliate_program_page:first_name')}
                className="APFFormInput"
                name="first_name"
                onChange={handleAffiliateProgramFormChange}
                error={Boolean(affiliateProgramErrors?.errors?.first_name)}
              />
              {affiliateProgramErrors?.errors && (
                <FormErrors
                  errorMsgs={affiliateProgramErrors}
                  errorField="first_name"
                />
              )}
            </Grid>
            <Grid item lg={6} md={12} sm={12}>
              <TextField
                value={affiliateProgramForm.last_name || ''}
                required
                variant="filled"
                fullWidth
                label={t('fe_er_affiliate_program_page:last_name')}
                className="APFFormInput"
                name="last_name"
                onChange={handleAffiliateProgramFormChange}
                error={Boolean(affiliateProgramErrors?.errors?.last_name)}
              />
              {affiliateProgramErrors?.errors && (
                <FormErrors
                  errorMsgs={affiliateProgramErrors}
                  errorField="last_name"
                />
              )}
            </Grid>
            <Grid item lg={6} md={12} sm={12}>
              <TextField
                value={affiliateProgramForm.phone || ''}
                variant="filled"
                fullWidth
                label={t('fe_er_affiliate_program_page:phone_number')}
                className="APFFormInput"
                name="phone"
                onChange={handleAffiliateProgramFormChange}
                error={Boolean(affiliateProgramErrors?.errors?.phone)}
              />
              {affiliateProgramErrors?.errors && (
                <FormErrors
                  errorMsgs={affiliateProgramErrors}
                  errorField="phone"
                />
              )}
            </Grid>
            <Grid item lg={6} md={12} sm={12}>
              <TextField
                value={affiliateProgramForm.extension || ''}
                variant="filled"
                fullWidth
                label={t('fe_er_affiliate_program_page:ext')}
                className="APFFormInput"
                name="extension"
                onChange={handleAffiliateProgramFormChange}
                error={Boolean(affiliateProgramErrors?.errors?.extension)}
              />
              {affiliateProgramErrors?.errors && (
                <FormErrors
                  errorMsgs={affiliateProgramErrors}
                  errorField="extension"
                />
              )}
            </Grid>
            <Grid item lg={6} md={12} sm={12}>
              <TextField
                value={affiliateProgramForm.email || ''}
                required
                variant="filled"
                fullWidth
                label={t('fe_er_affiliate_program_page:email_address')}
                className="APFFormInput"
                name="email"
                onChange={handleAffiliateProgramFormChange}
                type="email"
                error={Boolean(affiliateProgramErrors?.errors?.email)}
              />
              {affiliateProgramErrors?.errors && (
                <FormErrors
                  errorMsgs={affiliateProgramErrors}
                  errorField="email"
                />
              )}
            </Grid>
          </Grid>
          {envVars.RECAPTCHA_SITE_KEY && (
            <div className="APFFormRecaptchaContiner">
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={envVars.RECAPTCHA_SITE_KEY}
                onChange={handleRecaptchChange}
              />
            </div>
          )}
          <Button
            variant="contained"
            onClick={() => {
              handleSubmitForm(recaptchaRef);
            }}>
            {t('fe_er_affiliate_program_page:join_now')}
          </Button>
        </Box>
      </Box>
    </StyledContainer>
  );
};

export { AffiliateProgramForm };
